import axios from 'axios';

// Create axios instance for demo API
const demoApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8001',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Demo API endpoints
export const demoService = {
  // Get available demo courses
  async getCourses() {
    try {
      const response = await demoApi.get('/api/demo/courses');
      return response.data;
    } catch (error) {
      console.error('Error fetching demo courses:', error);
      throw error;
    }
  },

  // Get demo topics for a course
  async getTopics(courseId) {
    try {
      // Default to course ID 1 if not provided (for demo)
      const id = courseId || 1;
      const response = await demoApi.get(`/api/demo/topics/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching demo topics:', error);
      throw error;
    }
  },

  // Check rate limit status
  async checkRateLimit() {
    try {
      const response = await demoApi.get('/api/demo/rate-limit');
      return response.data;
    } catch (error) {
      console.error('Error checking rate limit:', error);
      throw error;
    }
  },

  // Generate demo exam
  async generateExam(params) {
    try {
      const response = await demoApi.post('/api/demo/generate-exam', {
        topics_include: params.topicsInclude,
        topic_ids: params.topicIds,
        difficulty: params.difficulty,
        mcq_marks: params.mcqMarks,
        extended_marks: params.extendedMarks,
      });
      return response.data;
    } catch (error) {
      // Handle rate limit error specifically
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        const errorData = error.response.data;
        const rateLimitError = new Error(errorData.detail || 'Rate limit exceeded. Please try again later.');
        rateLimitError.isRateLimit = true;
        rateLimitError.retryAfter = retryAfter ? parseInt(retryAfter) : 3600;
        throw rateLimitError;
      }
      console.error('Error generating demo exam:', error);
      throw error;
    }
  },

  // Check PDF generation status
  async checkPdfStatus(examId) {
    try {
      const response = await demoApi.get(`/api/demo/check-pdf/${examId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking PDF status:', error);
      throw error;
    }
  },

  // Get available marks for selected topics
  async getAvailableMarks(topics, difficulty, topicIds) {
    try {
      const response = await demoApi.post('/api/demo/available-marks', {
        topics_include: topics,
        topic_ids: topicIds && topicIds.length > 0 ? topicIds : undefined,
        difficulty: difficulty === 'all' ? 'all' : difficulty,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching available marks:', error);
      // Return zeros on error to prevent blocking UI
      return { mcq: 0, extended: 0 };
    }
  },

  // Get PDF download URL
  getPdfUrl(examId) {
    return `${demoApi.defaults.baseURL}/api/demo/download/${examId}`;
  },

  // Get answer for a demo question
  async getQuestionAnswer(demoQuestionId) {
    try {
      const response = await demoApi.get(`/api/demo/question/${demoQuestionId}/answer`);
      return response.data;
    } catch (error) {
      console.error('Error fetching question answer:', error);
      throw error;
    }
  },

  // Get all demo images
  async getImages() {
    try {
      const response = await demoApi.get('/api/demo/images');
      return response.data;
    } catch (error) {
      console.error('Error fetching demo images:', error);
      throw error;
    }
  },

  // Get demo image URL
  getImageUrl(imageId) {
    return `${demoApi.defaults.baseURL}/api/demo/images/${imageId}`;
  },
};

export default demoService;